// One Notebook cell's frame: the tick box, the kind, the title, "Open in chat",
// the agent's pending revision with Confirm and Undo, and the body the kind
// draws. Props and callbacks only.
import React from 'react'
import { Alert, Box, Button, Checkbox, Chip, Typography } from '@mui/material'
import { CARD_SX, HAIRLINE, INNER_RADIUS, PILL_RADIUS } from '../../../agent/components/surface'
import DisabledReason from '../../system/DisabledReason'
import { specAxes } from './notebookMessages'
import type { NotebookCell as Cell, NotebookCellKind, NotebookDecision } from './notebookTypes'

export const KIND_LABEL: Record<NotebookCellKind, string> = {
  table: 'Table',
  chart: 'Chart',
  composite: 'Composite',
  query: 'Query',
  script: 'Script',
  note: 'Note',
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace'

/** What the revision changes, in the words the cell kind uses. */
function RevisionDiff({ cell }: { cell: Cell }) {
  const pending = cell.pending
  if (!pending) return null
  const block = (label: string, text: string) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box component="pre" sx={{ m: 0, mt: 0.25, p: 1, border: HAIRLINE, borderRadius: INNER_RADIUS, fontFamily: MONO, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: 'background.paper' }}>
        {text || '(empty)'}
      </Box>
    </Box>
  )
  if (pending.kind === 'query') {
    return (
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {block('Before', String(cell.data.sql ?? ''))}
        {block('After', String(pending.data.sql ?? ''))}
      </Box>
    )
  }
  if (pending.kind === 'table' || pending.kind === 'chart') {
    const axes = (data: Record<string, unknown>) => {
      const a = specAxes({ ...cell, data, edit: undefined })
      return `side=${a.side}\ntop=${a.top}\nfilter=${a.filter}`
    }
    return (
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {block('Before', axes(cell.data))}
        {block('After', axes(pending.data))}
      </Box>
    )
  }
  const title = typeof pending.data.title === 'string' && pending.data.title ? ` (“${pending.data.title}”)` : ''
  return <Typography variant="body2" sx={{ mt: 0.5 }}>The agent rebuilt this table with new figures{title}.</Typography>
}

export interface NotebookCellProps {
  cell: Cell
  /** Why the viewer may not change the notebook. Undefined: they may. */
  readOnlyReason?: string
  ticked: boolean
  onToggleTick: () => void
  onDecide: (eventId: string, decision: NotebookDecision) => void
  onOpenInChat?: () => void
  onAddNoteBelow: () => void
  /** The pending revision drawn as the cell would draw it (composites). */
  pendingPreview?: React.ReactNode
  children: React.ReactNode
}

export const NotebookCell: React.FC<NotebookCellProps> = ({
  cell, readOnlyReason, ticked, onToggleTick, onDecide, onOpenInChat, onAddNoteBelow, pendingPreview, children,
}) => {
  const tickable = cell.kind !== 'script'
  return (
    <Box
      data-testid="notebook-cell"
      data-kind={cell.kind}
      data-cell-id={cell.id}
      data-pending={cell.pending ? 'true' : 'false'}
      sx={{ ...CARD_SX, overflow: 'visible', mb: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderBottom: HAIRLINE, minWidth: 0 }}>
        {tickable ? (
          <DisabledReason reason={readOnlyReason}>
            <Checkbox
              size="small"
              color="info"
              checked={ticked}
              onChange={onToggleTick}
              slotProps={{ input: { 'aria-label': `Use “${cell.title}” in a web app` } }}
              data-testid="notebook-cell-tick"
            />
          </DisabledReason>
        ) : (
          <Box sx={{ width: 38, flexShrink: 0 }} />
        )}
        <Chip size="small" variant="outlined" label={KIND_LABEL[cell.kind]} sx={{ borderRadius: PILL_RADIUS, flexShrink: 0 }} />
        <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0, fontFamily: cell.kind === 'script' ? MONO : undefined }}>
          {cell.title}
        </Typography>
        {cell.edit && (
          <Chip size="small" color="info" variant="outlined" label="Edited" sx={{ borderRadius: PILL_RADIUS, flexShrink: 0 }} data-testid="notebook-cell-edited" />
        )}
        {onOpenInChat && (
          <Button size="small" color="info" onClick={onOpenInChat} data-testid="notebook-open-in-chat" sx={{ flexShrink: 0 }}>
            Open in chat
          </Button>
        )}
        <DisabledReason reason={readOnlyReason}>
          <Button size="small" color="info" onClick={onAddNoteBelow} data-testid="notebook-add-note-below" sx={{ flexShrink: 0 }}>
            Add note below
          </Button>
        </DisabledReason>
      </Box>

      {cell.pending && (
        <Alert
          severity="info"
          icon={false}
          data-testid="notebook-pending"
          sx={{ borderRadius: 0, borderBottom: HAIRLINE, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="subtitle2">The agent changed this cell</Typography>
          <RevisionDiff cell={cell} />
          {pendingPreview}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <DisabledReason reason={readOnlyReason}>
              <Button size="small" variant="contained" color="info" onClick={() => onDecide(cell.pending!.eventId, 'confirmed')} data-testid="notebook-confirm">
                Confirm
              </Button>
            </DisabledReason>
            <DisabledReason reason={readOnlyReason}>
              <Button size="small" variant="outlined" color="info" onClick={() => onDecide(cell.pending!.eventId, 'undone')} data-testid="notebook-undo">
                Undo
              </Button>
            </DisabledReason>
          </Box>
        </Alert>
      )}

      <Box sx={{ p: 1.5, minWidth: 0 }}>{children}</Box>
    </Box>
  )
}

export default NotebookCell
